import React, { useState, useEffect, useMemo } from "react";
import { UrlApi } from "../../../services/apirest";
import { formatoPesoArgentino } from "../../../services/library";
import Navbar from "../../Navbar";
import Notify from "../../Notify";
import {
  Container,
  Box,
  Button,
  Chip,
  Stack,
  MenuItem,
  ListItemIcon,
  Typography,
} from "@mui/material";
import MaterialReactTable, {
  MRT_FullScreenToggleButton,
} from "material-react-table";
import { MRT_Localization_ES } from "material-react-table/locales/es";
import { createTheme, ThemeProvider, useTheme } from "@mui/material";
import AddCardIcon from "@mui/icons-material/AddCard";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import BlockIcon from "@mui/icons-material/Block";
import RefreshIcon from "@mui/icons-material/Refresh";
import PropaneIcon from "@mui/icons-material/Propane";
import moment from "moment";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import GenerarLinkPagoGranel from "./GenerarLinkPagoGranel";
import {
  verificarLinkGranel,
  anularLinkGranel,
  mensajeError,
} from "./services/linkGranelMp.service";

const colorEstado = {
  generado: "warning",
  pagado: "success",
  anulado: "default",
  expirado: "error",
};

function LinkPagoGranel() {
  const globalTheme = useTheme();
  const [links, setLinks] = useState([]);
  const [openGenerar, setOpenGenerar] = useState(false);
  const [refrescar, setRefrescar] = useState(false);
  const [fechaDesde, setFechaDesde] = useState(dayjs().startOf("month"));
  const [fechaHasta, setFechaHasta] = useState(dayjs());
  const [notify, setNotify] = useState({
    open: false,
    mensaje: "",
    severity: "success",
    color: "#D4EFDF",
  });

  const mostrarNotify = (mensaje, severity = "success") =>
    setNotify({
      open: true,
      mensaje,
      severity,
      color:
        severity === "success"
          ? "#D4EFDF"
          : severity === "error"
          ? "#F1948A"
          : "#F6DDCC",
    });

  const fechasValidas =
    fechaDesde?.isValid() &&
    fechaHasta?.isValid() &&
    !fechaDesde.isAfter(fechaHasta, "day");

  const obtenerLinks = () => {
    const desde = fechaDesde.format("YYYY-MM-DD");
    const hasta = fechaHasta.format("YYYY-MM-DD");
    fetch(UrlApi + "/granel_link_pago/xfecha/" + desde + "/" + hasta)
      .then((res) => res.json())
      .then((data) => setLinks(Array.isArray(data) ? data : []))
      .catch(() => setLinks([]));
  };

  useEffect(() => {
    if (fechasValidas) obtenerLinks();
  }, [refrescar, fechaDesde, fechaHasta]);

  const copiarLink = (link) => {
    if (!link) return;
    navigator.clipboard
      .writeText(link)
      .then(() => mostrarNotify("Link copiado al portapapeles"))
      .catch(() => mostrarNotify("No se pudo copiar el link", "error"));
  };

  const abrirLink = (link) => {
    if (link) window.open(link, "_blank", "noopener,noreferrer");
  };

  // La verificación la resuelve la API de MP: consulta la order, valida que
  // corresponda al registro (order y monto) y actualiza el estado en la base.
  const verificarPago = async (row) => {
    if (!row.order_id) {
      mostrarNotify("El link no tiene order_id para verificar", "warning");
      return;
    }
    try {
      const data = await verificarLinkGranel(row.id_empresa, row.id);
      switch (data?.accion) {
        case "pagado":
          mostrarNotify("Pago confirmado. Estado actualizado a PAGADO");
          break;
        case "expirado":
          mostrarNotify("El link venció sin pago. Estado actualizado a EXPIRADO", "warning");
          break;
        case "monto_no_coincide":
          mostrarNotify("El monto pagado no coincide con el del link. Revisar en Mercado Pago", "error");
          break;
        case "ignorada":
          mostrarNotify(data.motivo || "No se pudo verificar el link", "warning");
          break;
        default:
          mostrarNotify(
            `Todavía no hay un pago aprobado (estado: ${data?.order_status || "sin datos"})`,
            "warning"
          );
      }
      setRefrescar((v) => !v);
    } catch (err) {
      console.error("Error verificando pago:", err);
      mostrarNotify(mensajeError(err, "No se pudo verificar el pago"), "error");
    }
  };

  // La anulación la resuelve la API de MP: cancela la order (para que el link
  // deje de poder pagarse) y anula el registro.
  const anularLink = async (row) => {
    if (!window.confirm(`¿Anular el link de pago N° ${row.id}? El cliente ya no podrá pagarlo.`)) {
      return;
    }
    try {
      await anularLinkGranel(row.id_empresa, row.id);
      mostrarNotify("Link anulado");
    } catch (err) {
      console.error("Error anulando link:", err);
      mostrarNotify(mensajeError(err, "No se pudo anular el link"), "error");
    } finally {
      setRefrescar((v) => !v);
    }
  };

  const columns = useMemo(
    () => [
      { header: "N°", accessorKey: "id", size: 60 },
      { header: "Cliente", accessorKey: "cliente", size: 200 },
      {
        header: "Monto",
        accessorFn: (row) => formatoPesoArgentino(row.monto),
        size: 110,
      },
      { header: "Referencia", accessorKey: "referencia", size: 120 },
      {
        header: "Pedido",
        accessorKey: "id_pedido",
        size: 80,
        Cell: ({ cell }) => (cell.getValue() ? `#${cell.getValue()}` : "—"),
      },
      {
        header: "Estado",
        accessorKey: "estado",
        size: 110,
        Cell: ({ cell }) => (
          <Chip
            label={cell.getValue()}
            color={colorEstado[cell.getValue()] || "default"}
            size="small"
          />
        ),
      },
      {
        header: "Fecha",
        accessorFn: (row) =>
          row.created_at ? moment(row.created_at).format("DD-MM-YYYY HH:mm") : "",
        size: 130,
      },
    ],
    []
  );

  const tableTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: globalTheme.palette.mode,
          primary: globalTheme.palette.primary,
          info: { main: "rgb(255,122,0)" },
          background: {
            default:
              globalTheme.palette.mode === "light"
                ? "rgb(234, 239, 241)"
                : "#000",
          },
        },
        typography: {
          button: { textTransform: "none", fontSize: "1rem" },
        },
        components: {
          MuiTooltip: {
            styleOverrides: { tooltip: { fontSize: "0.8rem" } },
          },
        },
      }),
    [globalTheme]
  );

  const muiSearchTextFieldProps = {
    placeholder: "Buscar..",
    sx: { minWidth: "18rem", backgroundColor: "#ffffff" },
    variant: "outlined",
    color: "success",
    size: "small",
  };

  const initialState = {
    density: "compact",
    showGlobalFilter: true,
    pagination: { pageIndex: 0, pageSize: 10 },
  };

  const datePickerSlotProps = {
    textField: {
      size: "small",
      error: !fechasValidas,
      sx: { width: 170, backgroundColor: "#ffffff" },
    },
  };

  const tableHeadCellProps = {
    align: "center",
    sx: {
      fontSize: 16,
      color: "white",
      fontWeight: "normal",
      backgroundColor: "#2471A3",
      borderColor: "black",
    },
  };

  return (
    <div>
      <Navbar />
      <Container maxWidth="xl">
        <Box mt={2} boxShadow={3}>
          <ThemeProvider theme={tableTheme}>
            <MaterialReactTable
              enableColumnResizing
              columns={columns}
              data={links}
              initialState={initialState}
              positionActionsColumn="last"
              enableToolbarInternalActions={true}
              muiTableHeadCellProps={tableHeadCellProps}
              muiTableBodyCellProps={{
                align: "left",
                sx: {
                  backgroundColor: "#ffffff",
                  color: "black",
                  borderColor: "black",
                },
              }}
              enableColumnActions={false}
              muiSearchTextFieldProps={muiSearchTextFieldProps}
              enableDensityToggle={false}
              layoutMode="grid"
              localization={MRT_Localization_ES}
              enableColumnDragging={false}
              enableRowActions
              displayColumnDefOptions={{
                "mrt-row-actions": { size: 60, grow: false },
              }}
              renderTopToolbarCustomActions={() => (
                <Stack direction="row" spacing={1} m={1}>
                  <Typography
                    variant="h5"
                    fontWeight={600}
                    color="#1F618D"
                    alignSelf="center"
                    whiteSpace="nowrap"
                    pr={1}
                    display="flex"
                    alignItems="center"
                    gap={1}
                  >
                    <PropaneIcon fontSize="large" />
                    Granel
                  </Typography>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<AddCardIcon />}
                    onClick={() => setOpenGenerar(true)}
                  >
                    Generar link de pago
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => setRefrescar((v) => !v)}
                  >
                    Actualizar
                  </Button>
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale="es"
                  >
                    <DatePicker
                      label="Desde"
                      value={fechaDesde}
                      onChange={(v) => setFechaDesde(v)}
                      maxDate={fechaHasta}
                      format="DD-MM-YYYY"
                      slotProps={datePickerSlotProps}
                    />
                    <DatePicker
                      label="Hasta"
                      value={fechaHasta}
                      onChange={(v) => setFechaHasta(v)}
                      minDate={fechaDesde}
                      format="DD-MM-YYYY"
                      slotProps={datePickerSlotProps}
                    />
                  </LocalizationProvider>
                </Stack>
              )}
              renderToolbarInternalActions={({ table }) => (
                <MRT_FullScreenToggleButton table={table} />
              )}
              renderRowActionMenuItems={({ closeMenu, row }) => {
                const r = row.original;
                return [
                  <MenuItem
                    key="copiar"
                    disabled={!r.link || r.estado !== "generado"}
                    onClick={() => {
                      copiarLink(r.link);
                      closeMenu();
                    }}
                  >
                    <ListItemIcon>
                      <ContentCopyIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    Copiar link
                  </MenuItem>,
                  <MenuItem
                    key="abrir"
                    disabled={!r.link || r.estado !== "generado"}
                    onClick={() => {
                      abrirLink(r.link);
                      closeMenu();
                    }}
                  >
                    <ListItemIcon>
                      <OpenInNewIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    Abrir link
                  </MenuItem>,
                  <MenuItem
                    key="verificar"
                    disabled={r.estado !== "generado"}
                    onClick={() => {
                      verificarPago(r);
                      closeMenu();
                    }}
                  >
                    <ListItemIcon>
                      <FactCheckIcon fontSize="small" color="success" />
                    </ListItemIcon>
                    Verificar pago
                  </MenuItem>,
                  <MenuItem
                    key="anular"
                    disabled={r.estado !== "generado"}
                    onClick={() => {
                      anularLink(r);
                      closeMenu();
                    }}
                  >
                    <ListItemIcon>
                      <BlockIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    Anular link
                  </MenuItem>,
                ];
              }}
            />
          </ThemeProvider>
        </Box>
      </Container>

      {openGenerar && (
        <GenerarLinkPagoGranel
          open={openGenerar}
          onClose={() => setOpenGenerar(false)}
          onGenerado={() => setRefrescar((v) => !v)}
        />
      )}

      {notify.open && (
        <Notify
          mensaje={notify.mensaje}
          open={notify.open}
          color={notify.color}
          severity={notify.severity}
          handleClose={() => setNotify({ ...notify, open: false })}
        />
      )}
    </div>
  );
}

export default LinkPagoGranel;
